import * as z from 'zod';

import { PaymentDueId, PropertyId, ReportableEventId } from '../BrandedIds';
import { zodJSInstantDate } from '../dates/BrandedDate';
import { zodDateOrString } from '../utils';
import { InspectionType } from './Inspection';
import { NonPerformanceCode } from './NonPerformanceCode';

const ReportableEventDate = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('plainDate'),
    date: zodDateOrString,
  }),
  z.object({
    type: z.literal('timestamp'),
    timestamp: zodDateOrString,
  }),
  z.object({
    type: z.literal('plainDateRange'),
    startDate: zodDateOrString,
    endDate: zodDateOrString.optional(),
  }),
]);
export type ReportableEventDate = z.infer<typeof ReportableEventDate>;

const BaseReportableEvent = z.object({
  id: ReportableEventId,
  createdAt: zodJSInstantDate,
  updatedAt: zodJSInstantDate,
  deletedAt: zodJSInstantDate.optional(),

  /** Date the event actually occurred (TODO: #9105 remove once migration is done) */
  date: ReportableEventDate.optional(),

  /** @deprecated Use {@link ReportableEvent.date} instead */
  eventTimestamp: zodDateOrString.optional(),
});

// ------------------------------------ //
// --- REPORTABLE EVENT DEFINITIONS --- //
// ------------------------------------ //

/** Event type strings — add new types here and in ReportableEventsConfig below */
const REPORTABLE_EVENT_TYPES = [
  'appraisalOrdered',
  'inspectionOrdered',
  'reinstatement',
  'probate',
  'militaryIndulgence',
  'contestedLitigatedForeclosure',
  'assignment',
  'bankruptcyPlanConfirmed',
  'foreclosureSaleScheduled',
  'breachLetterSent',
  'completeBorrowerResponsePackage',
  'shortSaleApprovedOfferReceived',
  'assumption',
  'chargeOff',
  'mortgageRelease',
  'bankruptcyPropertySurrendered',
  'thirdPartySale',
  'shortSaleApprovedMarketingProperty',
  'refinance',
  'chapter13BankruptcyPlanPostPetition',
  'judgmentOrDecreeEntered',
  'foreclosureSaleContinued',
  'preFileMediation',
  'repaymentPlanInReview',
  'appealOfLoanModification',
  'bankruptcyCourtClearanceObtained',
  'dateAdvisedPropertyListedForSale',
  'solicitationLetterSent',
  'workoutInReview',
  'shortSaleInReview',
  'ineligibleForShortSale',
  'modificationInReview',
  'boardedDateOfFirstDelinquency',
  'pastDueMilestone',
] as const;

/** Union type of all reportable event types */
export type ReportableEventType = (typeof REPORTABLE_EVENT_TYPES)[number];

/** Factory for simple exception types that only need the base date field */
function createBasicReportableEvent<T extends ReportableEventType>(typeName: T) {
  return BaseReportableEvent.extend({ type: z.literal(typeName) });
}

export type ReportableEventDateKind = 'plainDate' | 'plainDateRange';

/** Ensures each config key K has schema with same type = K */
type ReportableEventsConfigType = {
  [K in ReportableEventType]: {
    readonly schema: z.ZodObject<{ type: z.ZodLiteral<K> }>;
    readonly nonPerformanceCode?: NonPerformanceCode;
    readonly dateType: ReportableEventDateKind;
  };
};

export const InspectionOrderedReason = z.enum(['collections', 'foreclosure', 'lossMitigation', 'bankruptcy', 'other']);
export type InspectionOrderedReason = z.infer<typeof InspectionOrderedReason>;

export const PAST_DUE_MILESTONE_DAYS = [30, 60, 90, 120] as const;
export const PastDueMilestoneDays = z.literal(PAST_DUE_MILESTONE_DAYS);
export type PastDueMilestoneDays = z.infer<typeof PastDueMilestoneDays>;

/**
 * Single source of truth for reportable events and their schemas - used by frontend and backend.
 * Add new events to REPORTABLE_EVENT_TYPES and here
 */
const ReportableEventsConfig = {
  appraisalOrdered: {
    schema: BaseReportableEvent.extend({
      type: z.literal('appraisalOrdered'),
      propertyId: PropertyId,
      vendorName: z.string(),
      appraisalType: z.enum(['bpo', 'appraisal']).optional(),
    }),
    nonPerformanceCode: 'BPO Ordered',
    dateType: 'plainDate',
  },
  inspectionOrdered: {
    schema: BaseReportableEvent.extend({
      type: z.literal('inspectionOrdered'),
      propertyId: PropertyId,
      inspectionType: InspectionType,
      inspectionReason: InspectionOrderedReason,
      vendorName: z.string(),
    }),
    nonPerformanceCode: undefined,
    dateType: 'plainDate',
  },
  reinstatement: {
    schema: createBasicReportableEvent('reinstatement'),
    nonPerformanceCode: 'Reinstatement',
    dateType: 'plainDate',
  },
  probate: { schema: createBasicReportableEvent('probate'), nonPerformanceCode: 'Probate', dateType: 'plainDateRange' },
  militaryIndulgence: {
    schema: createBasicReportableEvent('militaryIndulgence'),
    nonPerformanceCode: 'Military Indulgence',
    dateType: 'plainDateRange',
  },
  contestedLitigatedForeclosure: {
    schema: createBasicReportableEvent('contestedLitigatedForeclosure'),
    nonPerformanceCode: 'Contested/Litigated Foreclosure',
    dateType: 'plainDateRange',
  },
  assignment: {
    schema: createBasicReportableEvent('assignment'),
    nonPerformanceCode: 'Assignment',
    dateType: 'plainDateRange',
  },
  bankruptcyPlanConfirmed: {
    schema: createBasicReportableEvent('bankruptcyPlanConfirmed'),
    nonPerformanceCode: 'Bankruptcy Plan Confirmed',
    dateType: 'plainDate',
  },
  foreclosureSaleScheduled: {
    schema: createBasicReportableEvent('foreclosureSaleScheduled'),
    nonPerformanceCode: 'Foreclosure Sale Scheduled',
    dateType: 'plainDate',
  },
  breachLetterSent: {
    schema: createBasicReportableEvent('breachLetterSent'),
    nonPerformanceCode: 'Breach Letter Sent',
    dateType: 'plainDate',
  },
  completeBorrowerResponsePackage: {
    schema: createBasicReportableEvent('completeBorrowerResponsePackage'),
    nonPerformanceCode: 'Complete Borrower Response Package',
    dateType: 'plainDate',
  },
  shortSaleApprovedOfferReceived: {
    schema: createBasicReportableEvent('shortSaleApprovedOfferReceived'),
    nonPerformanceCode: 'Short Sale Approved/Offer Received',
    dateType: 'plainDate',
  },
  assumption: {
    schema: createBasicReportableEvent('assumption'),
    nonPerformanceCode: 'Assumption',
    dateType: 'plainDateRange',
  },
  chargeOff: {
    schema: createBasicReportableEvent('chargeOff'),
    nonPerformanceCode: 'Charge\u2011off',
    dateType: 'plainDate',
  },
  mortgageRelease: {
    schema: createBasicReportableEvent('mortgageRelease'),
    nonPerformanceCode: 'Mortgage Release',
    dateType: 'plainDateRange',
  },
  bankruptcyPropertySurrendered: {
    schema: createBasicReportableEvent('bankruptcyPropertySurrendered'),
    nonPerformanceCode: 'Bankruptcy\u2013Property Surrendered',
    dateType: 'plainDate',
  },
  thirdPartySale: {
    schema: createBasicReportableEvent('thirdPartySale'),
    nonPerformanceCode: 'Third\u2011Party Sale',
    dateType: 'plainDate',
  },
  shortSaleApprovedMarketingProperty: {
    schema: createBasicReportableEvent('shortSaleApprovedMarketingProperty'),
    nonPerformanceCode: 'Short Sale Approved/Marketing Property',
    dateType: 'plainDate',
  },
  refinance: {
    schema: createBasicReportableEvent('refinance'),
    nonPerformanceCode: 'Refinance',
    dateType: 'plainDate',
  },
  chapter13BankruptcyPlanPostPetition: {
    schema: createBasicReportableEvent('chapter13BankruptcyPlanPostPetition'),
    nonPerformanceCode: 'Chapter 13 Bankruptcy Plan - Post-Petition Period Utilize',
    dateType: 'plainDateRange',
  },
  judgmentOrDecreeEntered: {
    schema: createBasicReportableEvent('judgmentOrDecreeEntered'),
    nonPerformanceCode: 'Judgment or Decree Entered',
    dateType: 'plainDate',
  },
  foreclosureSaleContinued: {
    schema: createBasicReportableEvent('foreclosureSaleContinued'),
    nonPerformanceCode: 'Foreclosure Sale Continued',
    dateType: 'plainDate',
  },
  preFileMediation: {
    schema: createBasicReportableEvent('preFileMediation'),
    nonPerformanceCode: 'Pre-File Mediation/ Mediation',
    dateType: 'plainDateRange',
  },
  repaymentPlanInReview: {
    schema: createBasicReportableEvent('repaymentPlanInReview'),
    nonPerformanceCode: 'Repayment Plan in Review',
    dateType: 'plainDate',
  },
  appealOfLoanModification: {
    schema: createBasicReportableEvent('appealOfLoanModification'),
    nonPerformanceCode: 'Appeal of Loan Modification',
    dateType: 'plainDate',
  },
  bankruptcyCourtClearanceObtained: {
    schema: createBasicReportableEvent('bankruptcyCourtClearanceObtained'),
    nonPerformanceCode: 'Bankruptcy Court Clearance Obtained/Stay Lifted',
    dateType: 'plainDate',
  },
  dateAdvisedPropertyListedForSale: {
    schema: createBasicReportableEvent('dateAdvisedPropertyListedForSale'),
    nonPerformanceCode: 'Date Advised Property Listed for Sale',
    dateType: 'plainDate',
  },
  solicitationLetterSent: {
    schema: createBasicReportableEvent('solicitationLetterSent'),
    nonPerformanceCode: 'Solicitation Letter Sent',
    dateType: 'plainDate',
  },
  workoutInReview: {
    schema: createBasicReportableEvent('workoutInReview'),
    nonPerformanceCode: 'Workout in Review',
    dateType: 'plainDate',
  },
  shortSaleInReview: {
    schema: createBasicReportableEvent('shortSaleInReview'),
    nonPerformanceCode: 'Short Sale in Review',
    dateType: 'plainDate',
  },
  ineligibleForShortSale: {
    schema: createBasicReportableEvent('ineligibleForShortSale'),
    nonPerformanceCode: 'Ineligible for Short Sale',
    dateType: 'plainDate',
  },
  modificationInReview: {
    schema: createBasicReportableEvent('modificationInReview'),
    nonPerformanceCode: 'Modification in Review',
    dateType: 'plainDate',
  },
  boardedDateOfFirstDelinquency: {
    schema: createBasicReportableEvent('boardedDateOfFirstDelinquency'),
    nonPerformanceCode: undefined,
    dateType: 'plainDate',
  },
  pastDueMilestone: {
    schema: BaseReportableEvent.extend({
      type: z.literal('pastDueMilestone'),
      milestone: PastDueMilestoneDays,
      oldestOutstandingPaymentDueId: PaymentDueId, // The payment due that drove the delinquency
      oldestOutstandingPaymentDueDate: zodDateOrString,
    }),
    nonPerformanceCode: undefined,
    dateType: 'plainDate',
  },
} as const satisfies ReportableEventsConfigType;

const AllReportableEvents = REPORTABLE_EVENT_TYPES.map((type) => ReportableEventsConfig[type].schema);

type ReportableEventSchemaElement = (typeof AllReportableEvents)[number];
type ReportableEventInputSchemaElement = (typeof AllReportableEventInputs)[number];

export const ReportableEvent = z.discriminatedUnion(
  'type',
  AllReportableEvents as [ReportableEventSchemaElement, ...ReportableEventSchemaElement[]],
);
export type ReportableEvent = z.infer<typeof ReportableEvent>;

// -------------- //
// --- INPUTS --- //
// -------------- //

type ReportableEventInputOf<T extends z.ZodObject<{}>> =
  T extends z.ZodObject<infer S>
    ? z.ZodObject<{
        // TODO #9105 remove eventTimestamp once migration is done
        [K in keyof S as K extends 'createdAt' | 'updatedAt' | 'deletedAt' | 'eventTimestamp'
          ? never
          : K]: K extends 'id'
          ? z.ZodOptional<S[K]>
          : S[K] extends z.core.$ZodType<any, any, z.core.$ZodTypeInternals<any, any>>
            ? S[K]
            : never;
      }>
    : never;

function inputOf<T extends z.ZodObject<{}>>(event: T): ReportableEventInputOf<T> {
  // TODO #9105 remove eventTimestamp once migration is done
  return event.omit({ id: true, createdAt: true, updatedAt: true, deletedAt: true, eventTimestamp: true }).extend({
    /**
     * Event ID
     *
     * - If not provided:
     *   - handler will generate an ID while inserting a new event.
     * - If provided:
     *   - If an event with the provided ID already exists:
     *     - handler will update the event with the provided ID.
     *   - If an event with the provided ID does not exist:
     *     - handler will insert a new event with the provided ID.
     */
    id: ReportableEventId.optional(),
  }) as unknown as ReportableEventInputOf<T>;
}

const AllReportableEventInputs = AllReportableEvents.map((schema) => inputOf(schema));

export const ReportableEventInput = z.discriminatedUnion(
  'type',
  AllReportableEventInputs as [ReportableEventInputSchemaElement, ...ReportableEventInputSchemaElement[]],
);
export type ReportableEventInput = z.infer<typeof ReportableEventInput>;

// -------------------------- //
// --- INDIVIDUAL SCHEMAS --- //
// -------------------------- //

// for use where a specific event type is needed

export type InspectionOrderedReportableEvent = z.infer<typeof ReportableEventsConfig.inspectionOrdered.schema>;

export type PastDueMilestoneReportableEvent = z.infer<typeof ReportableEventsConfig.pastDueMilestone.schema>;

// ---------------- //
// --- MAPPINGS --- //
// ---------------- //

/**
 * Map of basic reportable event types to their date type (plainDate or plainDateRange)
 */
export const ReportableEventTypeDateType: Record<ReportableEventType, ReportableEventDateKind> = Object.fromEntries(
  REPORTABLE_EVENT_TYPES.map((type) => [type, ReportableEventsConfig[type].dateType]),
) as Record<ReportableEventType, ReportableEventDateKind>;
